import { Chip } from '@heroui/react';
import { useTranslation } from 'react-i18next';

type IndustryItem = {
  eyebrow: string;
  title: string;
  description: string;
};

const industryIcons = [
  'ri-scissors-line',
  'ri-car-washing-line',
  'ri-restaurant-2-line',
  'ri-hotel-bed-line',
  'ri-heart-pulse-line',
  'ri-building-4-line',
  'ri-hammer-line',
  'ri-run-line',
  'ri-graduation-cap-line',
  'ri-briefcase-4-line',
  'ri-shopping-bag-3-line',
  'ri-settings-5-line',
] as const;

export default function IndustryFocus() {
  const { t } = useTranslation();
  const items = t('home.industryFocus.items', { returnObjects: true }) as IndustryItem[];

  return (
    <section className="relative z-10 overflow-hidden bg-transparent py-20 text-[#171717] md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 text-center md:mb-16">
          <Chip
            variant="flat"
            className="mb-5 border border-brand-green/20 bg-[rgba(255,255,255,0.82)] px-4 py-2 text-sm font-semibold text-brand-green shadow-[0_10px_25px_rgba(163,230,53,0.08)]"
            startContent={
              <span className="mr-2 inline-flex items-center">
                <i className="ri-community-line text-base" />
              </span>
            }
          >
            {t('home.industryFocus.badge')}
          </Chip>
          <h2 className="mb-6 text-4xl font-caprasimo text-[#111111] md:text-5xl">
            {t('home.industryFocus.title')}
          </h2>
          <p className="mx-auto max-w-3xl text-lg leading-relaxed text-[#536274] md:text-xl">
            {t('home.industryFocus.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {items.map((item, index) => (
            <article
              key={item.title}
              className="group relative overflow-hidden rounded-[28px] border border-[rgba(124,156,193,0.16)] bg-[rgba(255,255,255,0.68)] p-7 shadow-[0_16px_36px_rgba(91,126,168,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-brand-green/30 hover:shadow-[0_22px_42px_rgba(91,126,168,0.14)]"
            >
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-green/40 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute right-0 top-0 h-24 w-24 -translate-y-6 translate-x-6 rounded-full bg-brand-green/10 blur-2xl" />

              <div className="relative z-10">
                <div className="mb-5 flex items-center justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-green/15 text-brand-green shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] transition-all duration-300 group-hover:bg-brand-green group-hover:text-[#0d0d0d]">
                    <i className={`${industryIcons[index] ?? 'ri-briefcase-4-line'} text-2xl`} />
                  </div>
                  <span className="rounded-full bg-brand-green/12 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d8a28]">
                    {item.eyebrow}
                  </span>
                </div>

                <h3 className="text-[1.5rem] font-caprasimo leading-tight text-[#171717] transition-colors duration-300 group-hover:text-brand-green">
                  {item.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-[#58697d] md:text-[15px]">
                  {item.description}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
